import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter, provideNativeDateAdapter } from '@angular/material/core';
import { MAT_DATE_RANGE_SELECTION_STRATEGY, MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { LanguageService } from '@core/services/language.service';
import { addDays } from '@core/utils/date.utils';
import { combineLatest, debounceTime, map, Subject } from 'rxjs';
import { DaysModeEnum } from '../../_enums/days-mode.enum';
import { DateRangeStrategyManagerService } from './date-range-strategy.manager.service';
import { PlannerCustomRangeSelectionStrategy } from './mat-date-range-selection.strategy';

export type DateRangeStruct = {
  start: Date | null;
  end: Date | null;
}

@Component({
  selector: 'planer-range-selection',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './planer-range-selection.component.html',
  styleUrl: './planer-range-selection.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: PlannerCustomRangeSelectionStrategy,
    },
    provideNativeDateAdapter(),
    DateRangeStrategyManagerService
  ],
})
export class PlanerRangeSelectionComponent implements OnChanges, OnInit {
  private readonly destroyRef = inject(DestroyRef);

  startDatePicker$ = new Subject<MatDatepickerInputEvent<any>>();
  endDatePicker$ = new Subject<MatDatepickerInputEvent<any>>();
  selectedDateStart?: Date;
  selectedDateEnd?: Date;

  @Input() selectedDateRange?: DateRangeStruct;
  @Input() selectedDaysMode?: DaysModeEnum;

  @Output() selectedDateRangeChanged = new EventEmitter<{ start: Date, end: Date }>()


  constructor(private readonly strategyManagerService: DateRangeStrategyManagerService,
    private readonly languageService: LanguageService,
    private readonly dateAdapter: DateAdapter<any>,
  ) {
  }

  ngOnInit(): void {
    combineLatest([this.startDatePicker$, this.endDatePicker$]).pipe(
      debounceTime(100),
      takeUntilDestroyed(this.destroyRef),
      map(([a$, b$]) => ({
        start: a$,
        end: b$
      })),
    )
      .subscribe((data) => {
        if (data.start.value && data.end.value) {
          this.selectedDateRangeChanged.emit({ start: data.start.value!, end: data.end.value! })
        }
      });

    this.languageService.language$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((language) => {
        this.dateAdapter.setLocale(language);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedDateRange']) {
      this.selectedDateStart = this.selectedDateRange?.start!;
      // w planerze jest tak, że jako datę końcową operujemy datą następnego dnia o godzinie 00:00 
      // więc żeby kalendarz działał dobrze musimy tutaj odjąć jeden dzień.
      this.selectedDateEnd = addDays(this.selectedDateRange?.end!, -1);
    }
    if (changes['selectedDaysMode']) {
      this.strategyManagerService.currentSelectedStrategy = this.selectedDaysMode!;
    }
  }
}
